import { Link } from 'react-router';
import { Plus, Users } from 'lucide-react';
import { resolvePostVisibility, type PostVisibility } from '@repo/shared-types';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/src/components/ui/table';
import { useAdminBlogPosts, useNow } from '../hooks/useBlog';
import { formatDate, formatDateTime } from '../lib/adminFormat';

const VISIBILITY_LABEL: Record<PostVisibility, string> = {
  draft: 'Bản nháp',
  scheduled: 'Đã lên lịch',
  live: 'Đang hiển thị',
};

const VISIBILITY_VARIANT: Record<PostVisibility, 'muted' | 'warning' | 'success'> = {
  draft: 'muted',
  scheduled: 'warning',
  live: 'success',
};

/** Never reads the raw `status` — a post held for a future date is
 *  `status: 'published'` in the DB while nobody can reach it. */
function VisibilityChip({ post, now }: { post: { status: 'draft' | 'published'; publishedAt: string | null }; now: Date }) {
  const visibility = resolvePostVisibility(post, now);
  return (
    <Badge variant={VISIBILITY_VARIANT[visibility]}>
      {visibility === 'scheduled'
        ? `${VISIBILITY_LABEL.scheduled} · ${formatDateTime(post.publishedAt as string)}`
        : VISIBILITY_LABEL[visibility]}
    </Badge>
  );
}

export function BlogPostsPage() {
  const { data: posts, isLoading, error } = useAdminBlogPosts();
  // Ticking clock: a post scheduled for the near future should flip its chip
  // to "Đang hiển thị" on its own while this list sits open, not only after
  // a manual reload.
  const now = useNow();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">Bài viết</h1>
          <p className="text-sm text-muted-foreground mt-1">Quản lý bài viết blog công khai trên trang chủ.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/blog/authors">
              <Users className="mr-1.5 h-4 w-4" /> Tác giả
            </Link>
          </Button>
          <Button asChild>
            <Link to="/blog/new">
              <Plus className="mr-1.5 h-4 w-4" /> Bài viết mới
            </Link>
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tiêu đề</TableHead>
              <TableHead>Trạng thái</TableHead>
              <TableHead>Tác giả</TableHead>
              <TableHead>Xuất bản</TableHead>
              <TableHead>Cập nhật</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Đang tải...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-destructive">
                  Không thể tải danh sách bài viết. Vui lòng thử lại sau.
                </TableCell>
              </TableRow>
            ) : !posts || posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Chưa có bài viết nào.
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell className="py-2">
                    <Link to={`/blog/${post.id}`} className="font-medium text-surface-900 hover:underline">
                      {post.title}
                    </Link>
                  </TableCell>
                  <TableCell className="py-2">
                    <VisibilityChip post={post} now={now} />
                  </TableCell>
                  <TableCell className="py-2 text-surface-600">{post.author.name}</TableCell>
                  <TableCell className="py-2 tabular-nums text-surface-600">
                    {post.publishedAt ? formatDate(post.publishedAt) : '—'}
                  </TableCell>
                  <TableCell className="py-2 tabular-nums text-surface-600">{formatDate(post.updatedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
